import {ERROR} from './api'

const DEFAULT_BIZ_NAME = 'ERROR_SPL_SMYQ_HEXIAOYANQ'
const STORAGE_KEY = 'YAN_QUAN_LOG_RETRY_QUEUE'
const INITIAL_RETRY_DELAY = 3000
const MAX_RETRY_DELAY = 60000

const state = {
    queue: [],
    timer: null,
    timerDue: 0,
    flushing: false,
    busyCount: 0,
    initialized: false,
}

function getDelay(delay) {
    if (typeof delay === 'number') return Math.max(0, delay)
    return state.busyCount > 0 ? 1500 : 800
}

function getRetryDelay(retryCount = 0) {
    return Math.min(MAX_RETRY_DELAY, INITIAL_RETRY_DELAY * Math.pow(2, Math.max(0, retryCount - 1)))
}

function now() {
    return Date.now()
}

function createLogId() {
    return `${now()}_${Math.random().toString(16).slice(2)}`
}

function canUseStorage() {
    return typeof window !== 'undefined' && window.localStorage
}

function readPersistedQueue() {
    if (!canUseStorage()) return []
    try {
        const raw = window.localStorage.getItem(STORAGE_KEY)
        if (!raw) return []
        const list = JSON.parse(raw)
        return Array.isArray(list) ? list : []
    } catch (err) {
        console.error('yanquan log queue read failed', err)
        return []
    }
}

function persistQueue() {
    if (!canUseStorage()) return
    try {
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state.queue))
    } catch (err) {
        console.error('yanquan log queue persist failed', err)
    }
}

function hydrateQueue() {
    if (state.initialized) return
    state.initialized = true
    state.queue = readPersistedQueue()
}

function normalizeItem(item = {}) {
    return {
        id: item.id || createLogId(),
        bizName: item.bizName || DEFAULT_BIZ_NAME,
        fields: item.fields || '',
        retryCount: Number(item.retryCount || 0),
        createdAt: item.createdAt || now(),
        nextRetryAt: Number(item.nextRetryAt || 0),
        lastError: item.lastError || '',
    }
}

function getErrorText(err) {
    if (!err) return ''
    return String(
        err.msg ||
        err.message ||
        err.name ||
        err.status ||
        err.code ||
        (err.response && err.response.data && (err.response.data.msg || err.response.data.message)) ||
        ''
    )
}

function registerRetryTriggers() {
    if (typeof window === 'undefined') return
    window.addEventListener('online', () => scheduleYanQuanLogFlush(0))
    if (typeof document === 'undefined') return
    document.addEventListener('visibilitychange', () => {
        if (!document.hidden) {
            scheduleYanQuanLogFlush(0)
        }
    })
}

hydrateQueue()
registerRetryTriggers()

export function setYanQuanLogBusinessBusy(isBusy) {
    if (isBusy) {
        state.busyCount += 1
        return
    }
    state.busyCount = Math.max(0, state.busyCount - 1)
    scheduleYanQuanLogFlush(0)
}

export function enqueueYanQuanLog(fields, options = {}) {
    hydrateQueue()
    state.queue.push(normalizeItem({
        bizName: options.bizName || DEFAULT_BIZ_NAME,
        fields,
    }))
    persistQueue()
    scheduleYanQuanLogFlush(options.delay)
}

export function scheduleYanQuanLogFlush(delay) {
    hydrateQueue()
    const targetDelay = getDelay(delay)
    const due = now() + targetDelay
    if (state.timer && due >= state.timerDue) return
    if (state.timer) {
        clearTimeout(state.timer)
    }
    state.timerDue = due
    state.timer = setTimeout(() => {
        state.timer = null
        state.timerDue = 0
        flushYanQuanLogQueue()
    }, targetDelay)
}

export async function flushYanQuanLogQueue(force = false) {
    hydrateQueue()
    if (state.flushing) return
    if (!force && state.busyCount > 0) {
        scheduleYanQuanLogFlush(1500)
        return
    }

    state.flushing = true
    try {
        while (state.queue.length) {
            if (!force && state.busyCount > 0) break
            const item = normalizeItem(state.queue[0])
            const waitTime = item.nextRetryAt - now()
            if (!force && waitTime > 0) {
                scheduleYanQuanLogFlush(waitTime)
                break
            }
            try {
                await ERROR('customer/log/error', {
                    bizName: item.bizName,
                    fields: item.fields,
                })
                state.queue.shift()
                persistQueue()
            } catch (logErr) {
                console.error('yanquan log report failed', logErr)
                state.queue.shift()
                const retryItem = normalizeItem({
                    ...item,
                    retryCount: item.retryCount + 1,
                    nextRetryAt: now() + getRetryDelay(item.retryCount + 1),
                    lastError: getErrorText(logErr),
                })
                state.queue.push(retryItem)
                persistQueue()
                scheduleYanQuanLogFlush(retryItem.nextRetryAt - now())
                break
            }
            if (state.queue.length) {
                await new Promise(resolve => setTimeout(resolve, 100))
            }
        }
    } finally {
        state.flushing = false
        if (state.queue.length) {
            const firstItem = normalizeItem(state.queue[0])
            const retryDelay = Math.max(0, firstItem.nextRetryAt - now())
            scheduleYanQuanLogFlush(state.busyCount > 0 ? 1500 : retryDelay || 800)
        }
    }
}
